/**
 * Which body paragraphs of the open document already own a paragraph bookmark: the
 * one read both paragraph-naming passes share, `tryBookmarkingAllParagraphs` in
 * `paragraph-bookmarks.ts` and `nameParagraphsForEngine` in
 * `paragraph-bookmarks-engine.ts`.
 *
 * WHY one bookmark listing for the whole body, not one per paragraph
 *   Word answers `Range.getBookmarks` at a fixed cost per call, whatever the size of
 *   the range, and the naming pass runs before every document fetch.
 *   EXAMPLE, Word Online, 1,102-paragraph document, measured 2026-09-28
 *     one `getBookmarks` per paragraph, 1,102 calls          44.7 s
 *     one body-wide `getBookmarks`, then each name's paragraph  0.47 s
 *   CONSEQUENCE of the per-paragraph read
 *     a large document kept the agent's document request waiting past its 180 s limit.
 */

/** A paragraph bookmark's range and the paragraph that range starts in, queued before the sync that resolves both. */
interface BookmarkFirstParagraphProbe {
    bookmarkName: string;
    bookmarkRange: Word.Range;
    firstParagraph: Word.Paragraph;
}

/** A resolved probe's paragraph id and the queued comparison of the bookmark's range with that paragraph. */
interface BookmarkToFirstParagraphCheck {
    bookmarkName: string;
    firstParagraphId: string;
    bookmarkToFirstParagraphRelation: OfficeExtension.ClientResult<Word.LocationRelation>;
}

/**
 * WHEN a bookmark's range lies on the paragraph it starts in
 *   CASES
 *     - `inside`       -> the usual shape, the bookmark covers the paragraph's content
 *     - `equal`        -> the bookmark covers exactly the paragraph range
 *     - `insideStart`  -> the bookmark starts at the paragraph's first character
 *   CONSEQUENCE
 *     the paragraph owns that name.
 * WHEN any other relation (`contains`, `before`, `after`, `overlaps…`, `none`)
 *   CONSEQUENCE
 *     the name drifted onto or across other paragraphs after an edit; the paragraph is treated as unnamed.
 */
function bookmarkLiesOnParagraph(locationRelation: string): boolean {
    return locationRelation === Word.LocationRelation.inside || locationRelation === Word.LocationRelation.equal || locationRelation === Word.LocationRelation.insideStart;
}

/** Queues the lookup of one name's range and of the first paragraph that range starts in. */
function queueBookmarkFirstParagraphProbe(context: Word.RequestContext, bookmarkName: string): BookmarkFirstParagraphProbe {
    const bookmarkRange = context.document.getBookmarkRangeOrNullObject(bookmarkName);
    const firstParagraph = bookmarkRange.paragraphs.getFirstOrNullObject();
    firstParagraph.load("uniqueLocalId");
    return { bookmarkName, bookmarkRange, firstParagraph };
}

/**
 * NULL
 *   WHEN the name's range, or the first paragraph of that range, comes back as a
 *   null object from `getBookmarkRangeOrNullObject` / `getFirstOrNullObject`
 *   CONSEQUENCE
 *     the name owns no paragraph, and its range is left out of the comparison round,
 *     where a null range would fail the whole sync.
 */
function probeResolvedToAParagraph(bookmarkFirstParagraphProbe: BookmarkFirstParagraphProbe): boolean {
    return !bookmarkFirstParagraphProbe.firstParagraph.isNullObject;
}

/** Queues the comparison of a resolved probe's bookmark range with the paragraph it starts in. */
function queueBookmarkToFirstParagraphCheck(bookmarkFirstParagraphProbe: BookmarkFirstParagraphProbe): BookmarkToFirstParagraphCheck {
    return {
        bookmarkName: bookmarkFirstParagraphProbe.bookmarkName,
        firstParagraphId: bookmarkFirstParagraphProbe.firstParagraph.uniqueLocalId,
        bookmarkToFirstParagraphRelation: bookmarkFirstParagraphProbe.bookmarkRange.compareLocationWith(bookmarkFirstParagraphProbe.firstParagraph.getRange()),
    };
}

/**
 * Resolves which body paragraphs own one of `paragraphBookmarkNames`.
 *
 * SYNC ROUNDS, in order
 *   1. resolve each name's range and the first paragraph that range starts in
 *   2. compare each resolved range with that paragraph
 *
 * WHY the first paragraph
 *   A name that lies on a paragraph starts in it, so the paragraph its range starts in
 *   is the only one it can own. A name stretched across two paragraphs starts in the
 *   first, compares with it as `contains` or an overlap, and owns neither: the same
 *   answer the per-paragraph read gave, checked side by side in Word Online on
 *   2026-09-28 against a deleted name, a name stretched across two paragraphs and a
 *   freshly inserted unnamed paragraph.
 *
 * RETURNS
 *   For every name that lies on a body paragraph, the `uniqueLocalId` of that
 *   paragraph; a name missing from the map lies on no paragraph.
 */
export async function readParagraphIdOwningEachBookmark(context: Word.RequestContext, paragraphBookmarkNames: string[]): Promise<Map<string, string>> {
    const bookmarkFirstParagraphProbes = paragraphBookmarkNames.map((bookmarkName) => queueBookmarkFirstParagraphProbe(context, bookmarkName));
    await context.sync();

    const bookmarkToFirstParagraphChecks = bookmarkFirstParagraphProbes.filter(probeResolvedToAParagraph).map(queueBookmarkToFirstParagraphCheck);
    await context.sync();

    return new Map(
        bookmarkToFirstParagraphChecks
            .filter((bookmarkToFirstParagraphCheck) => bookmarkLiesOnParagraph(bookmarkToFirstParagraphCheck.bookmarkToFirstParagraphRelation.value))
            .map((bookmarkToFirstParagraphCheck) => [bookmarkToFirstParagraphCheck.bookmarkName, bookmarkToFirstParagraphCheck.firstParagraphId]),
    );
}

/**
 * RETURNS
 *   The `uniqueLocalId` of every body paragraph some name lies on; a paragraph whose
 *   id is missing is unnamed.
 */
export async function readIdsOfParagraphsOwningABookmark(context: Word.RequestContext, paragraphBookmarkNames: string[]): Promise<Set<string>> {
    return new Set((await readParagraphIdOwningEachBookmark(context, paragraphBookmarkNames)).values());
}
